import type {
    AdapterSnapshot,
    Capabilities,
    TableAdapter,
    TableFetchParams,
} from '../../ports/table-adapter';
import { readPath } from '../shared/path';
import { AUTO, type FieldType } from './field';
import type { Filter, ResolvedFilter } from './filter';
import {
    derivePaging,
    lastPageIndex,
    type Paging,
    type PageRequest,
    type PageResult,
} from './paging';
import { applyFilters, applySearch, applySort } from './predicates';
import type { SortState } from './sort';

export abstract class BaseTableAdapter<T> implements TableAdapter<T> {
    #rows: T[] = [];
    #loading = false;
    #error: unknown = null;
    #sort: SortState | null = null;
    #filters: ResolvedFilter[] = [];
    #search = '';
    #page = 0;
    #totalCount: number | undefined;
    #paging: Paging = { offset: 0, hasNext: false, hasPrev: false };
    #request: PageRequest = { kind: 'first' };
    #cursors: { next?: unknown; prev?: unknown } = {};
    #listeners = new Set<() => void>();
    #requestId = 0;
    #forceFresh = false;
    #controller: AbortController | null = null;
    #pending: Promise<void> = Promise.resolve();
    #snap!: AdapterSnapshot<T>;
    #capsResolved = false;
    #pageSizeResolved: boolean;
    protected constructor(protected pageSize?: number) {
        this.#pageSizeResolved = pageSize !== undefined;
        this.#snap = {
            rows: [],
            loading: false,
            error: null,
            sort: null,
            filters: [],
            search: '',
            page: 0,
            hasNext: false,
            hasPrev: false,
            offset: 0,
            capabilities: {},
        };
        this.getSnapshot = this.getSnapshot.bind(this);
        this.subscribe = this.subscribe.bind(this);
        this.setSort = this.setSort.bind(this);
        this.setFilter = this.setFilter.bind(this);
        this.setFilters = this.setFilters.bind(this);
        this.setSearch = this.setSearch.bind(this);
        this.refresh = this.refresh.bind(this);
    }

    protected abstract fetchPage(
        params: TableFetchParams,
    ): Promise<PageResult<T>>;
    protected caps(): Capabilities {
        return {};
    }
    protected searchFieldPaths(): string[] {
        return [];
    }
    protected fieldFor(_key: string): FieldType {
        return AUTO;
    }
    protected read(row: T, key: string): unknown {
        return readPath(row, key);
    }
    applyDefaultPageSize(size: number): void {
        if (this.#pageSizeResolved) return;
        this.pageSize = size;
        this.#pageSizeResolved = true;
    }

    getPageSize(): number | undefined {
        return this.pageSize;
    }

    getSnapshot(): AdapterSnapshot<T> {
        if (!this.#capsResolved) {
            this.#capsResolved = true;
            this.#snap = { ...this.#snap, capabilities: this.caps() };
        }
        return this.#snap;
    }

    subscribe(listener: () => void): () => void {
        this.#listeners.add(listener);
        if (this.#requestId === 0)
            this.#pending = this.#load({ kind: 'first' });
        return () => this.#listeners.delete(listener);
    }

    setSort(sort: SortState | null): void {
        if (JSON.stringify(sort) === JSON.stringify(this.#sort)) return;
        this.#sort = sort;
        this.#reset();
    }

    canSortBy(key: string): boolean {
        try {
            this.fieldFor(key);
            return true;
        } catch {
            return false;
        }
    }

    setFilter(key: string, filter: Filter | null): void {
        const rest = this.#filters.filter((f) => f.key !== key);
        this.setFilters(filter ? [...rest, { ...filter, key }] : rest);
    }

    setFilters(filters: ResolvedFilter[]): void {
        if (JSON.stringify(filters) === JSON.stringify(this.#filters)) return;
        this.#filters = filters;
        this.#reset();
    }

    setSearch(text: string): void {
        if (text === this.#search) return;
        this.#search = text;
        this.#reset();
    }

    protected get currentPage(): number {
        return this.#page;
    }

    protected get currentRequest(): PageRequest {
        return this.#request;
    }

    /**
     * Builds the params for the request currently in effect (sort, filters,
     * search, page size) without the `signal`/`force` that only apply to a
     * real async fetch. Lets a subclass whose data is already in memory
     * compute a page result synchronously via the same rules `fetchPage`
     * uses, instead of going through it.
     */
    protected buildFetchParams(
        req: PageRequest = this.#request,
    ): Omit<TableFetchParams, 'signal' | 'force'> {
        return {
            page: req,
            pageSize: this.pageSize,
            sort: this.#sort,
            filters: this.#filters,
            search: this.#search,
        };
    }

    /**
     * Commits an already-known page result synchronously, bypassing the
     * async fetchPage round-trip entirely. `fetchPage` always returns a
     * Promise (even `StaticTableAdapter`'s `Promise.resolve(...)`, since
     * there's no real I/O), and `await`-ing an already-resolved promise
     * still yields to the microtask queue - an artificial delay between
     * "rows changed" and "snapshot updated" that a subclass backed by
     * in-memory data doesn't need to pay.
     */
    protected commitPageResult(
        res: PageResult<T>,
        req: PageRequest = this.#request,
    ): void {
        this.#controller?.abort();
        this.#requestId += 1;
        this.#request = req;
        this.#applyResult(res, req);
    }

    protected moveNext(): void {
        const token = this.#cursors.next;
        if (token === undefined) return;
        this.#page += 1;
        this.#pending = this.#load({ kind: 'after', token });
    }

    protected movePrev(force = false): void {
        if (this.#page === 0) return;
        if (this.#page === 1) {
            this.moveFirst(force);
            return;
        }
        const token = this.#cursors.prev;
        if (token === undefined) return;
        this.#page -= 1;
        this.#pending = this.#load({ kind: 'before', token }, force);
    }

    protected moveFirst(force = false): void {
        this.#page = 0;
        this.#pending = this.#load({ kind: 'first' }, force);
    }

    protected moveLast(): void {
        this.#page =
            lastPageIndex(this.#totalCount, this.pageSize) ?? this.#page;
        this.#pending = this.#load({ kind: 'last' });
    }

    protected moveAt(index: number): void {
        if (index < 0) return;
        this.#page = index;
        this.#pending = this.#load(
            index === 0 ? { kind: 'first' } : { kind: 'at', index },
        );
    }

    refresh(): void {
        this.#forceFresh = true;
        this.#pending = this.#doRefresh();
    }

    whenSettled(): Promise<void> {
        return this.#pending;
    }

    /**
     * Routes an error from a subclass's synchronous, in-memory page
     * computation (e.g. `StaticTableAdapter#setRows`) through the same
     * error state `#load`'s catch block applies, so callers only ever
     * observe adapter failures via `snapshot.error`, never an uncaught
     * throw out of a synchronous method.
     */
    protected reportError(err: unknown): void {
        this.#error = err;
        this.#loading = false;
        this.#emit();
    }

    #reset(): void {
        this.#cursors = {};
        this.#page = 0;
        this.#pending = this.#load({ kind: 'first' });
    }

    async #doRefresh(): Promise<void> {
        await this.#load(this.#request, true);
        if (this.#rows.length === 0 && this.#paging.hasPrev && this.#page > 0) {
            // Force this fallback fetch too: it runs immediately after a
            // mutation-triggered refresh, so a cached response for the
            // adjacent page could show stale data right after the edit.
            this.movePrev(true);
            await this.#pending;
        }
    }

    async #load(req: PageRequest, force = false): Promise<void> {
        const id = ++this.#requestId;
        this.#request = req;
        this.#controller?.abort();
        const controller = new AbortController();
        this.#controller = controller;
        this.#loading = true;
        this.#emit();
        try {
            const res = await this.fetchPage({
                page: req,
                pageSize: this.pageSize,
                sort: this.#sort,
                filters: this.#filters,
                search: this.#search,
                signal: controller.signal,
                force: force || this.#forceFresh,
            });
            if (id !== this.#requestId) return;
            this.#applyResult(res, req);
        } catch (err) {
            if (id !== this.#requestId) return;
            this.#error = err;
            this.#loading = false;
            this.#emit();
        }
    }

    #applyResult(res: PageResult<T>, req: PageRequest): void {
        const caps = this.caps();
        const readValue = (row: T, key: string): unknown => this.read(row, key);
        let rows = res.rows;
        if (!caps.filter) {
            rows = applyFilters(rows, this.#filters, readValue);
            rows = applySearch(
                rows,
                this.#search,
                this.searchFieldPaths(),
                readValue,
            );
        }
        if (!caps.sort) {
            const comparator = this.#sort
                ? this.fieldFor(this.#sort.key).sort
                : AUTO.sort;
            rows = applySort(rows, this.#sort, readValue, comparator);
        }
        this.#cursors = { next: res.nextToken, prev: res.prevToken };
        this.#totalCount = res.totalCount;
        this.#paging = derivePaging(
            this.#page,
            this.pageSize,
            res,
            req.kind === 'last',
        );
        this.#rows = rows;
        this.#loading = false;
        this.#error = null;
        this.#emit();
    }

    #emit(): void {
        this.#snap = {
            rows: this.#rows,
            loading: this.#loading,
            error: this.#error,
            sort: this.#sort,
            filters: this.#filters,
            search: this.#search,
            page: this.#page,
            hasNext: this.#paging.hasNext,
            hasPrev: this.#paging.hasPrev,
            offset: this.#paging.offset,
            totalCount: this.#totalCount,
            pageCount: this.#paging.pageCount,
            capabilities: this.caps(),
        };
        for (const listener of this.#listeners) listener();
    }
}
